import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "locales";
import { BackgroundMode } from "hooks/useBlurController";
import "./style.scss";

interface Props {
  currentMode: BackgroundMode;
  onSelectMode: (mode: BackgroundMode) => void;
  onSelectImage: (imageUrl: string) => void;
  onClose?: () => void;
  className?: string;
}

const PRESET_BACKGROUNDS = [
  { id: "beach", color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", label: "Gradient 1" },
  { id: "sunset", color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", label: "Gradient 2" },
  { id: "ocean", color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)", label: "Gradient 3" },
  { id: "forest", color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)", label: "Gradient 4" },
  { id: "night", color: "linear-gradient(135deg, #0c3483 0%, #a2b6df 100%)", label: "Gradient 5" },
];

export default function BackgroundSelector({ currentMode, onSelectMode, onSelectImage, onClose, className }: Props) {
  const { t } = useTranslation();
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;
      onSelectImage(url);
    },
    [onSelectImage]
  );

  const cls = ["background-selector"];
  if (className) cls.push(className);

  return (
    <div className={cls.join(" ")}>
      <div className="background-selector__title">{t("Background")}</div>
      {onClose && (
        <button
          type="button"
          className="background-selector__close"
          onClick={onClose}
          aria-label={t("Close")}
        >
          ×
        </button>
      )}
      <div className="background-selector__options">
        <button
          type="button"
          className={`background-selector__option${currentMode === "none" ? " __active" : ""}`}
          onClick={() => onSelectMode("none")}
        >
          <div className="background-selector__preview __none">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
            </svg>
          </div>
          <span>{t("None")}</span>
        </button>

        <button
          type="button"
          className={`background-selector__option${currentMode === "blur" ? " __active" : ""}`}
          onClick={() => onSelectMode("blur")}
        >
          <div className="background-selector__preview __blur" />
          <span>{t("Blur")}</span>
        </button>

        {PRESET_BACKGROUNDS.map((bg) => (
          <button
            key={bg.id}
            type="button"
            className={`background-selector__option${currentMode === "image" && selectedPresetId === bg.id ? " __active" : ""}`}
            onClick={() => {
              setSelectedPresetId(bg.id);
              // For gradient presets, create a canvas-based image
              const canvas = document.createElement("canvas");
              canvas.width = 640;
              canvas.height = 480;
              const ctx = canvas.getContext("2d");
              if (ctx) {
                const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
                const colors = bg.color.match(/#[0-9a-f]{6}/gi) || ["#667eea", "#764ba2"];
                gradient.addColorStop(0, colors[0]);
                gradient.addColorStop(1, colors[1] || colors[0]);
                ctx.fillStyle = gradient;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                onSelectImage(canvas.toDataURL());
              }
            }}
          >
            <div className="background-selector__preview" style={{ background: bg.color }} />
            <span>{bg.label}</span>
          </button>
        ))}

        <button
          type="button"
          className="background-selector__option"
          onClick={() => fileInput.current?.click()}
        >
          <div className="background-selector__preview __upload">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
            </svg>
          </div>
          <span>{t("Upload")}</span>
        </button>
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
    </div>
  );
}
